/**
 * TanStack Query hooks over the typed api-client (E10-F01-T04).
 *
 * This is where the two halves meet (architecture.md §5: "Состояние сервера —
 * TanStack Query, заменяет ручной app.state + localStorage"):
 *   - the client (`apiClient`) does transport + auth + normalization;
 *   - TanStack Query does caching + invalidation, keyed by `qk` (queryKeys.ts).
 *
 * List domains projects/clients/emails are cached; mutations invalidate the
 * matching domain prefix so a create/update/delete refreshes the affected list
 * (acceptance: "кэширует списки … и инвалидируется после мутаций").
 *
 * The api-client never throws — it returns `ApiResult`. But TanStack Query needs
 * a *thrown* error to mark a query/mutation failed. `unwrap` is the single place
 * that converts the failed result into a thrown `ApiResultError` (carrying the
 * typed `ApiErrorDetail`), so feature code reads `query.error.detail.message`
 * and the "no throw in UI" guarantee is preserved everywhere else.
 *
 * Entity payloads are typed by the *caller* (`useProjectsList<Project[]>()`),
 * since the domain types (`Project`, `Email`, `Client`) land with their feature
 * epics (E02/E04/E05). Until then these hooks are the stable cache contract.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseQueryResult,
  type UseMutationOptions,
  type UseMutationResult,
  type QueryKey,
} from '@tanstack/react-query';
import { apiClient, unwrap, type ApiResultError } from './client.js';
import { qk, type ListParams, type QueryDomain } from './queryKeys.js';

/** Endpoint path per list domain (REST routes, architecture.md §6). */
const DOMAIN_PATH: Record<QueryDomain, string> = {
  projects: '/projects',
  clients: '/clients',
  emails: '/emails',
};

/**
 * Generic list query for a cached domain. Returns a TanStack `UseQueryResult`
 * whose `error` (on failure) is an `ApiResultError` carrying `detail.message`.
 *
 * @param domain  one of projects/clients/emails (drives both key + endpoint)
 * @param params  optional filters/search/pagination (part of the cache key)
 */
export function useListQuery<TData>(
  domain: QueryDomain,
  params?: ListParams,
  options?: Omit<UseQueryOptions<TData, ApiResultError, TData, QueryKey>, 'queryKey' | 'queryFn'>,
): UseQueryResult<TData, ApiResultError> {
  return useQuery<TData, ApiResultError, TData, QueryKey>({
    queryKey: qk[domain].list(params),
    queryFn: async ({ signal }) =>
      unwrap(await apiClient.get<TData>(DOMAIN_PATH[domain], { query: params, signal })),
    ...options,
  });
}

/** Cached list of projects (`GET /projects`). Typed by the caller. */
export function useProjectsList<TData>(
  params?: ListParams,
  options?: Omit<UseQueryOptions<TData, ApiResultError, TData, QueryKey>, 'queryKey' | 'queryFn'>,
): UseQueryResult<TData, ApiResultError> {
  return useListQuery<TData>('projects', params, options);
}

/** Cached Address-Book list of clients (`GET /clients`). */
export function useClientsList<TData>(
  params?: ListParams,
  options?: Omit<UseQueryOptions<TData, ApiResultError, TData, QueryKey>, 'queryKey' | 'queryFn'>,
): UseQueryResult<TData, ApiResultError> {
  return useListQuery<TData>('clients', params, options);
}

/** Cached Inbox list of emails (`GET /emails`). */
export function useEmailsList<TData>(
  params?: ListParams,
  options?: Omit<UseQueryOptions<TData, ApiResultError, TData, QueryKey>, 'queryKey' | 'queryFn'>,
): UseQueryResult<TData, ApiResultError> {
  return useListQuery<TData>('emails', params, options);
}

/**
 * Mutation that automatically invalidates the given domain(s) on success, so
 * the affected lists refetch. Pass the `mutationFn` that performs the write via
 * `apiClient` + `unwrap`; on success the prefix keys (`qk[domain].all`) are
 * invalidated.
 *
 * @example
 *   const archive = useDomainMutation(
 *     (id: number) => unwrap(apiClient.post(`/projects/${id}/archive`)),
 *     ['projects'],
 *   );
 *   archive.mutate(42); // → invalidates ['projects'] on success
 */
export function useDomainMutation<TData, TVariables>(
  mutationFn: (vars: TVariables) => Promise<TData>,
  invalidates: readonly QueryDomain[],
  options?: Omit<UseMutationOptions<TData, ApiResultError, TVariables>, 'mutationFn'>,
): UseMutationResult<TData, ApiResultError, TVariables> {
  const queryClient = useQueryClient();
  return useMutation<TData, ApiResultError, TVariables>({
    mutationFn,
    ...options,
    // TanStack Query v5 onSuccess signature is
    // (data, variables, onMutateResult, context) — forward all four so a
    // caller's own onSuccess receives the exact values it expects.
    onSuccess: async (data, vars, onMutateResult, context) => {
      await Promise.all(
        invalidates.map((domain) => queryClient.invalidateQueries({ queryKey: qk[domain].all })),
      );
      await options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}
